// src/pages/OnlineOrdersPage.jsx
import React, { useState, useEffect } from 'react';
import { ShoppingBag, ChevronDown, ChevronUp, X, MapPin, Store } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { ordersManageApi, storeApi } from '../api';

const STATUS_CONFIG = {
  pending:    { label: '待指派',  color: 'bg-yellow-100 text-yellow-700' },
  assigned:   { label: '待接单',  color: 'bg-blue-100 text-blue-700' },
  confirmed:  { label: '已接单',  color: 'bg-indigo-100 text-indigo-700' },
  preparing:  { label: '备货中',  color: 'bg-orange-100 text-orange-700' },
  delivering: { label: '配送中',  color: 'bg-purple-100 text-purple-700' },
  ready:      { label: '待自提',  color: 'bg-cyan-100 text-cyan-700' },
  completed:  { label: '已完成',  color: 'bg-green-100 text-green-700' },
  cancelled:  { label: '已取消',  color: 'bg-gray-100 text-gray-500' }
};

const OnlineOrdersPage = () => {
  const { currentUser } = useApp();
  const isHQ = currentUser?.store_type === 'headquarters';
  const isManager = currentUser?.role === 'manager';

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  // 指派弹窗
  const [showAssign, setShowAssign] = useState(false);
  const [assignOrderId, setAssignOrderId] = useState(null);
  const [assignOrderNo, setAssignOrderNo] = useState('');
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [assigning, setAssigning] = useState(false);

  const filterTabs = isHQ
    ? [['', '全部'], ['pending', '待指派'], ['assigned', '待接单'], ['confirmed', '已接单'], ['preparing', '备货中'], ['delivering', '配送中'], ['ready', '待自提'], ['completed', '已完成']]
    : [['', '全部'], ['assigned', '待接单'], ['confirmed', '已接单'], ['preparing', '备货中'], ['delivering', '配送中'], ['ready', '待自提'], ['completed', '已完成']];

  useEffect(() => {
    loadOrders();
    if (isHQ) loadBranches();
  }, [filterStatus]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const res = await ordersManageApi.getList(filterStatus ? { order_status: filterStatus } : {});
      if (res.success) setOrders(res.data);
    } catch (err) {
      console.error('加载订单失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadBranches = async () => {
    try {
      const res = await storeApi.getList();
      if (res.success) setBranches(res.data.filter(s => s.type === 'branch' && s.status === 'active'));
    } catch (err) { console.error(err); }
  };

  const toggleDetail = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  // 指派
  const openAssign = (order) => {
    setAssignOrderId(order.id);
    setAssignOrderNo(order.order_no);
    setSelectedBranch('');
    setShowAssign(true);
  };

  const handleAssign = async () => {
    if (!selectedBranch) { alert('请选择配送方式'); return; }
    setAssigning(true);
    try {
      const isHQDelivery = selectedBranch === 'hq';
      const res = await ordersManageApi.assign(assignOrderId, {
        assigned_store_id: isHQDelivery ? currentUser.store_id : parseInt(selectedBranch),
        is_hq_delivery: isHQDelivery
      });
      if (res.success) {
        const info = res.data
          ? `\n${isHQDelivery ? '总店自行配送，全额收入' : `佣金：$${res.data.commission_amount}\n分店收入：$${res.data.branch_amount}`}`
          : '';
        alert(`指派成功！${info}`);
        setShowAssign(false);
        loadOrders();
      } else {
        alert(res.message || '指派失败');
      }
    } catch (err) {
      alert(err.response?.data?.message || '指派失败');
    } finally {
      setAssigning(false);
    }
  };

  // 操作按钮
  const handleAction = async (action, order) => {
    const confirmMap = {
      confirm: '确认接单？',
      prepare: '确认开始备货？备货后将扣减库存。',
      deliver: order.delivery_type === 'pickup' ? '确认商品已备好，通知用户自提？' : '确认开始配送？',
      complete: '确认订单已完成？完成后将自动结算。'
    };
    if (!window.confirm(confirmMap[action])) return;
    try {
      let res;
      if (action === 'confirm') res = await ordersManageApi.confirm(order.id);
      else if (action === 'prepare') res = await ordersManageApi.prepare(order.id);
      else if (action === 'deliver') res = await ordersManageApi.deliver(order.id);
      else if (action === 'complete') res = await ordersManageApi.complete(order.id);
      if (res.success) { alert(res.message); loadOrders(); }
      else alert(res.message || '操作失败');
    } catch (err) {
      alert(err.response?.data?.message || '操作失败');
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* 头部 */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">线上订单</h2>
          <p className="text-gray-600 mt-1">
            {isHQ ? '管理所有 H5 线上订单' : '处理分配给本店的订单'}
          </p>
        </div>
        <button onClick={loadOrders} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
          刷新
        </button>
      </div>

      {/* 状态筛选 */}
      <div className="flex flex-wrap gap-2">
        {filterTabs.map(([val, label]) => (
          <button
            key={val}
            onClick={() => setFilterStatus(val)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              filterStatus === val ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* 订单列表 */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">加载中...</p>
        </div>
      ) : orders.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <ShoppingBag className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500">暂无订单</p>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map(order => {
            const statusCfg = STATUS_CONFIG[order.order_status] || STATUS_CONFIG.pending;
            const isExpanded = expandedId === order.id;

            return (
              <div key={order.id} className="bg-white rounded-xl shadow overflow-hidden">
                <div
                  className="p-5 flex items-start justify-between cursor-pointer hover:bg-gray-50"
                  onClick={() => toggleDetail(order.id)}
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-100 p-2 rounded-lg mt-0.5">
                      <ShoppingBag className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2 flex-wrap gap-1">
                        <span className="font-semibold">{order.order_no}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusCfg.color}`}>
                          {statusCfg.label}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          order.delivery_type === 'pickup' ? 'bg-cyan-100 text-cyan-700' : 'bg-purple-100 text-purple-700'
                        }`}>
                          {order.delivery_type === 'pickup' ? '🏪 自提' : '🛵 配送'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-3 mt-1 text-sm text-gray-500 flex-wrap gap-1">
                        <span>{order.customer_name || '用户'} {order.customer_phone}</span>
                        <span>${parseFloat(order.total_amount).toFixed(2)}</span>
                        <span>{new Date(order.created_at).toLocaleDateString('zh-CN')}</span>
                      </div>
                      {order.assigned_store_name && (
                        <p className="text-xs text-blue-600 mt-1 flex items-center space-x-1">
                          <Store className="w-3 h-3" />
                          <span>指派：{order.assigned_store_name}</span>
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 ml-2" onClick={e => e.stopPropagation()}>
                    {/* 总店：指派（配送订单） */}
                    {isHQ && order.order_status === 'pending' && order.delivery_type === 'delivery' && (
                      <button onClick={() => openAssign(order)}
                        className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-blue-700">
                        指派配送
                      </button>
                    )}
                    {/* 总店：自提订单直接确认 */}
                    {isHQ && order.order_status === 'pending' && order.delivery_type === 'pickup' && (
                      <button onClick={() => handleAction('confirm', order)}
                        className="bg-indigo-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-indigo-700">
                        确认接单
                      </button>
                    )}
                    {/* 分店：接单 */}
                    {isManager && !isHQ && order.order_status === 'assigned' && (
                      <button onClick={() => handleAction('confirm', order)}
                        className="bg-indigo-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-indigo-700">
                        接单
                      </button>
                    )}
                    {/* 总店自配：直接接单 */}
                    {isHQ && order.order_status === 'assigned' && (
                      <button onClick={() => handleAction('confirm', order)}
                        className="bg-indigo-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-indigo-700">
                        确认接单
                      </button>
                    )}
                    {/* 备货 */}
                    {order.order_status === 'confirmed' && (
                      <button onClick={() => handleAction('prepare', order)}
                        className="bg-orange-500 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-orange-600">
                        开始备货
                      </button>
                    )}
                    {/* 配送/就绪 */}
                    {order.order_status === 'preparing' && (
                      <button onClick={() => handleAction('deliver', order)}
                        className="bg-purple-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-purple-700">
                        {order.delivery_type === 'pickup' ? '通知自提' : '开始配送'}
                      </button>
                    )}
                    {/* 完成 */}
                    {(order.order_status === 'delivering' || order.order_status === 'ready') && (
                      <button onClick={() => handleAction('complete', order)}
                        className="bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-green-700">
                        确认完成
                      </button>
                    )}
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </div>
                </div>

                {/* 展开详情 */}
                {isExpanded && (
                  <div className="border-t px-5 py-4 bg-gray-50 space-y-4">
                    {order.delivery_type === 'delivery' && order.delivery_address && (
                      <div className="flex items-start space-x-2 text-sm">
                        <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600">{order.delivery_address}</span>
                      </div>
                    )}
                    {order.delivery_type === 'pickup' && order.store_name && (
                      <div className="flex items-center space-x-2 text-sm">
                        <Store className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-gray-600">自提门店：{order.store_name}</span>
                      </div>
                    )}

                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-gray-500">
                          <th className="text-left py-1">商品</th>
                          <th className="text-right py-1">单价</th>
                          <th className="text-right py-1">数量</th>
                          <th className="text-right py-1">小计</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.items && order.items.map(item => (
                          <tr key={item.id} className="border-t border-gray-100">
                            <td className="py-2">{item.product_name}</td>
                            <td className="text-right py-2">${parseFloat(item.unit_price).toFixed(2)}</td>
                            <td className="text-right py-2">x{item.quantity}</td>
                            <td className="text-right py-2">${parseFloat(item.subtotal).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t border-gray-200">
                          <td colSpan="3" className="py-2 text-gray-500">配送费</td>
                          <td className="text-right py-2">${parseFloat(order.delivery_fee || 0).toFixed(2)}</td>
                        </tr>
                        <tr className="font-semibold">
                          <td colSpan="3" className="py-2">合计</td>
                          <td className="text-right py-2">${parseFloat(order.total_amount).toFixed(2)}</td>
                        </tr>
                      </tfoot>
                    </table>

                    {/* 结算信息 */}
                    {order.commission_rate > 0 && (
                      <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm">
                        <p className="font-medium text-blue-700 mb-1">利润分配</p>
                        <div className="flex justify-between text-blue-600">
                          <span>总店佣金（{(order.commission_rate * 100).toFixed(1)}%）</span>
                          <span>${parseFloat(order.commission_amount).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-green-600">
                          <span>分店收入（含配送费）</span>
                          <span>${parseFloat(order.branch_amount).toFixed(2)}</span>
                        </div>
                      </div>
                    )}
                    {order.commission_rate == 0 && order.assigned_store_name && (
                      <div className="bg-green-50 border border-green-100 rounded-lg p-3 text-sm">
                        <p className="text-green-700">🏪 总店自行配送，全额收入 ${parseFloat(order.total_amount).toFixed(2)}</p>
                      </div>
                    )}

                    {order.notes && <p className="text-sm text-gray-500">备注：{order.notes}</p>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* 指派弹窗 */}
      {showAssign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold">指派配送</h3>
              <button onClick={() => setShowAssign(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-500">订单号：{assignOrderNo}</p>
              <div>
                <label className="block text-sm font-medium mb-2">选择配送方式 *</label>
                <select
                  value={selectedBranch}
                  onChange={e => setSelectedBranch(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">请选择</option>
                  <option value="hq">🏪 总店自行配送（全额收入）</option>
                  {branches.map(b => (
                    <option key={b.id} value={b.id}>
                      {b.name}（佣金：{(b.commission_rate * 100).toFixed(1)}%）
                    </option>
                  ))}
                </select>
              </div>
              {selectedBranch === 'hq' && (
                <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-700">
                  总店自行配送，无需分佣，全额收入归总店
                </div>
              )}
              {selectedBranch && selectedBranch !== 'hq' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 text-sm text-yellow-700">
                  指派后分店店长将收到接单通知，请确认分店有相应库存
                </div>
              )}
            </div>
            <div className="p-6 border-t flex space-x-3">
              <button onClick={() => setShowAssign(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                取消
              </button>
              <button onClick={handleAssign} disabled={assigning}
                className={`flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg ${assigning ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}`}>
                {assigning ? '指派中...' : '确认指派'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnlineOrdersPage;